import { Box, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from '@mui/material';
import { useIntl } from 'react-intl';
import { VehicleTCO } from '../../../../calculations/vehicle/CostOfOwnership/calcTotalCostOfOwnership';
import isElectricVehicle from '../../../../calculations/vehicle/isElectricVehicle';
import isPHEV from '../../../../calculations/vehicle/isPHEV';
import { useUserPrefs } from '../../../../context/UserPrefsProvider';
import { ElectricVehicle, EquivalentFossilVehicle, GasVehicle } from '../../../../types';
import {
  formatAsDollars,
  formatAsDollarsPerGal,
  formatAsPercentRounded,
  formatAsThousands,
  formatVehicleDisplayName,
} from '../../../../utils/formatters';

type Props = {
  vehicle1: ElectricVehicle | GasVehicle | EquivalentFossilVehicle;
  vehicle2: ElectricVehicle | GasVehicle | EquivalentFossilVehicle;
  vehicle1Tco: VehicleTCO;
  vehicle2Tco: VehicleTCO;
};

export default function GasolineTable({ vehicle1, vehicle2, vehicle1Tco, vehicle2Tco }: Props) {
  const { formatMessage } = useIntl();

  const {
    userPrefs: {
      milesDrivenAnnually,
      monthsOfOwnership,
      electricMilesPortionForPhev,
      gasolinePriceInCentsPerGal,
    },
  } = useUserPrefs();

  const lifetimeMiles = (milesDrivenAnnually * monthsOfOwnership) / 12;

  const renderPHEVFuelSplitAdjustment = (fuelType: string) => {
    if (!['BEV', 'PHEV'].includes(fuelType.toUpperCase())) return 'N/A';
    return ['PHEV'].includes(fuelType.toUpperCase())
      ? formatAsPercentRounded(electricMilesPortionForPhev)
      : '100%';
  };

  return (
    <Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell component="th" scope="col">
              {formatMessage({ id: 'compare.table.description' })}
            </TableCell>
            <TableCell component="th" scope="col">
              {formatVehicleDisplayName(vehicle1)}
            </TableCell>
            <TableCell component="th" scope="col">
              {formatVehicleDisplayName(vehicle2)}
            </TableCell>
            <TableCell component="th" scope="col">
              {formatMessage({ id: 'compare.table.source' })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell component="th" scope="row">
              {formatMessage({ id: 'compare.table.gasoline.gasolineCost' })}
            </TableCell>
            <TableCell>
              {isElectricVehicle(vehicle1) && !isPHEV(vehicle1)
                ? 'N/A'
                : formatAsDollarsPerGal(gasolinePriceInCentsPerGal / 100)}
            </TableCell>
            <TableCell>
              {isElectricVehicle(vehicle2) && !isPHEV(vehicle2)
                ? 'N/A'
                : formatAsDollarsPerGal(gasolinePriceInCentsPerGal / 100)}
            </TableCell>
            <TableCell>User Input</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              {formatMessage({ id: 'compare.table.gasoline.mpgLabel' })}
            </TableCell>
            <TableCell>
              {isElectricVehicle(vehicle1) && !isPHEV(vehicle1)
                ? 'N/A'
                : formatMessage(
                    { id: 'compare.table.gasoline.mpg' },
                    { mpg: vehicle1.fossilFuelEfficiency },
                  )}
            </TableCell>
            <TableCell>
              {isElectricVehicle(vehicle2) && !isPHEV(vehicle2)
                ? 'N/A'
                : formatMessage(
                    { id: 'compare.table.gasoline.mpg' },
                    { mpg: vehicle2.fossilFuelEfficiency },
                  )}
            </TableCell>
            <TableCell />
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              {formatMessage({ id: 'compare.table.lifetimeMiles' })}
            </TableCell>
            <TableCell colSpan={2}>{formatAsThousands(lifetimeMiles)}</TableCell>
            <TableCell>{formatMessage({ id: 'compare.table.userInput' })}</TableCell>
          </TableRow>
          {(isPHEV(vehicle1) || isPHEV(vehicle2)) && (
            <TableRow>
              <TableCell component="th" scope="row">
                {formatMessage({ id: 'compare.table.phevPortion' })}
              </TableCell>
              <TableCell>{renderPHEVFuelSplitAdjustment(vehicle1.fuelType)}</TableCell>
              <TableCell>{renderPHEVFuelSplitAdjustment(vehicle2.fuelType)}</TableCell>
              <TableCell>{formatMessage({ id: 'compare.table.phevPortionExplainer' })}</TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell component="th" scope="row">
              {formatMessage({ id: 'compare.table.gasoline.totalGasolineCost' })}
            </TableCell>
            <TableCell>{formatAsDollars(vehicle1Tco.gasoline.total)}</TableCell>
            <TableCell>{formatAsDollars(vehicle2Tco.gasoline.total)}</TableCell>
            <TableCell>
              {formatMessage({
                id:
                  isPHEV(vehicle1) || isPHEV(vehicle2)
                    ? 'compare.table.gasoline.totalGasolineCostExplainer'
                    : 'compare.table.gasoline.totalGasolineCostExplainerPHEV',
              })}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </Box>
  );
}
